import { useTranslation } from "react-i18next";
import { currencyFormat } from "../utils";

function PriceEstimate({ priceCalculate, type }) {
  const { t } = useTranslation();

  const findText = (item) => {
    if (item?.type === "addtional-all-in-one") {
      return {
        text: "Toll and parking",
        icon: "fa-road",
        color: "text-blue-600",
      };
    } else if (item?.type === "price" && item?.date !== "Wedding") {
      return {
        text: item?.date,
        icon: "fa-calendar-day",
        color: "text-green-600",
      };
    } else if (item?.type === "additional") {
      if (item?.date?.includes("Umbrella")) {
        return {
          text: item?.date,
          icon: "fa-umbrella",
          color: "text-yellow-600",
        };
      } else if (item?.date?.includes("Baby Seat")) {
        return {
          text: item?.date,
          icon: "fa-baby",
          color: "text-pink-600",
        };
      } else if (item?.date?.includes("Phone Holder")) {
        return {
          text: item?.date,
          icon: "fa-mobile-screen",
          color: "text-purple-600",
        };
      } else if (item?.date?.includes("Gps")) {
        return {
          text: item?.date,
          icon: "fa-location-dot",
          color: "text-blue-500",
        };
      } else if (item?.date === "Wedding") {
        return {
          text: "Wedding",
          icon: "fa-asl-interpreting",
          color: "text-red-500",
        };
      } else {
        return {
          text: item?.date,
          icon: "fa-plus-circle",
          color: "text-gray-600",
        };
      }
    } else if (item?.type === "fees") {
      return {
        text: "Transaction fees + taxes",
        icon: "fa-receipt",
        color: "text-orange-600",
      };
    } else if (item?.type === "delivery_fee") {
      return {
        text: "Delivery fee",
        icon: "fa-truck-fast",
        color: "text-blue-600",
      };
    } else if (item?.type === "pickup_fee") {
      return {
        text: "Pickup Fee",
        icon: "fa-location-dot",
        color: "text-purple-600",
      };
    } else {
      return {
        text: item?.date || "Unknown",
        icon: "fa-circle-info",
        color: "text-gray-600",
      };
    }
  };

  return (
    <div className="w-full mx-auto">
      {/* Main Price Card */}
      <div className="overflow-hidden bg-white border border-gray-200 shadow-lg rounded-xl">
        {/* Header with Gradient */}
        <div className="relative px-6 py-4 overflow-hidden bg-gradient-to-r from-[#f5272b] to-red-600">
          {/* Decorative Pattern */}
          <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-12 -translate-y-12 bg-white rounded-full opacity-10"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 transform -translate-x-8 translate-y-8 bg-white rounded-full opacity-10"></div>

          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-white rounded-lg bg-opacity-20">
                <i className="text-2xl text-white fa-solid fa-file-invoice-dollar"></i>
              </div>
              <h3 className="text-2xl font-bold tracking-wide text-white">{t("price_estimate")}</h3>
            </div>
            <div className="items-center hidden gap-2 px-4 py-2 bg-white rounded-lg sm:flex bg-opacity-20 backdrop-blur-sm">
              <i className="text-white fa-solid fa-shield-check"></i>
              <span className="text-sm font-semibold text-white">Verified</span>
            </div>
          </div>
        </div>

        {/* Price Breakdown Table */}
        <div className="p-6 bg-gradient-to-b from-white to-gray-50">
          <div className="space-y-1">
            {priceCalculate?.prices?.map((item, idx) => {
              const itemInfo = findText(item);
              const isLast = idx === priceCalculate.prices.length - 1;

              return (
                <div key={idx} className={`group relative ${!isLast ? "border-b border-gray-200" : ""}`}>
                  <div className="flex items-center justify-between px-3 py-4 transition-all duration-200 rounded-lg hover:bg-white hover:shadow-sm">
                    {/* Left Side - Item Name */}
                    <div className="flex items-center flex-1 gap-3">
                      <div
                        className={`flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 group-hover:bg-gradient-to-br group-hover:from-gray-100 group-hover:to-gray-200 transition-all`}
                      >
                        <i className={`fa-solid ${itemInfo.icon} ${itemInfo.color} text-md`}></i>
                      </div>
                      <span className="text-2xl font-medium text-gray-700 transition-colors group-hover:text-gray-900">
                        {itemInfo.text}
                      </span>
                    </div>

                    {/* Right Side - Price */}
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-gray-900 group-hover:text-[#f5272b] transition-colors">
                        {currencyFormat(item?.price)}
                      </span>
                      <i className="text-gray-400 transition-opacity opacity-0 fa-solid fa-chevron-right group-hover:opacity-100"></i>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Total Section with Enhanced Design */}
          <div className="mt-6">
            <div className="relative p-6 overflow-hidden shadow-xl bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl">
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#f5272b] rounded-full opacity-10 transform translate-x-16 -translate-y-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#f5272b] rounded-full opacity-10 transform -translate-x-12 translate-y-12"></div>

              {/* Shine Effect */}
              <div className="absolute inset-0 transform -skew-x-12 bg-gradient-to-r from-transparent via-white to-transparent opacity-5"></div>

              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-[#f5272b] rounded-xl shadow-lg">
                    <i className="text-xl text-white fa-solid fa-coins"></i>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium tracking-wider text-gray-400 uppercase">
                      {t("estimate_total")}
                    </p>
                    <h4 className="text-3xl font-bold tracking-tight text-white">
                      {currencyFormat(priceCalculate?.total)}
                    </h4>
                  </div>
                </div>

                <div className="hidden sm:block">
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex items-center gap-2 px-3 py-1 bg-green-500 rounded-full bg-opacity-20">
                      <i className="text-xs text-green-400 fa-solid fa-circle-check"></i>
                      <span className="text-xs font-semibold text-green-400">Best Price</span>
                    </div>
                    <span className="text-xs text-gray-500">All inclusive</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-3 gap-3 mt-6">
            <div className="flex flex-col items-center p-3 bg-white border border-gray-200 rounded-lg">
              <i className="mb-2 text-2xl text-blue-500 fa-solid fa-shield-halved"></i>
              <span className="text-xs font-medium text-center text-gray-600">Secure Payment</span>
            </div>
            <div className="flex flex-col items-center p-3 bg-white border border-gray-200 rounded-lg">
              <i className="mb-2 text-2xl text-green-500 fa-solid fa-circle-check"></i>
              <span className="text-xs font-medium text-center text-gray-600">Transparent</span>
            </div>
            <div className="flex flex-col items-center p-3 bg-white border border-gray-200 rounded-lg">
              <i className="mb-2 text-2xl text-purple-500 fa-solid fa-headset"></i>
              <span className="text-xs font-medium text-center text-gray-600">24/7 Support</span>
            </div>
          </div>
        </div>
      </div>

      {/* Important Note */}
      <div className="mt-5">
        {type === "transfer_service" ? (
          <div className="relative overflow-hidden border-l-4 border-green-500 shadow-sm bg-gradient-to-r from-green-50 to-emerald-50 rounded-r-xl">
            <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
            <div className="relative z-10 flex items-start gap-4 p-5">
              <div className="flex-shrink-0">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500 shadow-sm">
                  <i className="fa-solid fa-circle-check text-xl text-white"></i>
                </div>
              </div>

              <div className="flex-1">
                <h5 className="mb-1 text-xl font-bold text-red-500">What's Included</h5>

                <p className="text-sm font-medium leading-relaxed text-gray-700">{t("the_price_includes")}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative overflow-hidden border-l-4 shadow-sm border-amber-500 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-r-xl">
            <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
            <div className="relative z-10 flex items-start gap-4 p-5">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg shadow-sm bg-amber-500">
                  <i className="text-lg text-white fa-solid fa-circle-exclamation"></i>
                </div>
              </div>
              <div className="flex-1">
                <h5 className="mb-1 text-sm font-bold text-amber-900">Important Notice</h5>
                <p className="text-sm leading-relaxed text-amber-800">{t("the_price_does_not")}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Additional Info Badge */}
      <div className="flex items-center justify-center gap-2 mt-4">
        <i className="text-xs text-gray-400 fa-solid fa-info-circle"></i>
        <span className="text-xs text-gray-500">Final price may vary based on additional services</span>
      </div>
    </div>
  );
}

export default PriceEstimate;

// import { useTranslation } from "react-i18next";
// import { currencyFormat } from "../utils";

// function PriceEstimate({ priceCalculate, type }) {
//   const { t } = useTranslation();
//   // const getLanguage =
//   //   typeof window !== undefined && localStorage.getItem("@wirasana_lang");
//   // const currentLanguage = JSON.parse(getLanguage);

//   return (
//     <>
//       <div className="estimate-box">
//         {/* description */}
//         <div className="estimate-description">
//           <div className="estimate-description__price">
//             <span>{t("price_estimate")}</span>
//           </div>
//           <div className="estimate-description__table">
//             {priceCalculate?.prices?.map((item, idx) => {
//               const findText = () => {
//                 if (item?.type === "delivery_fee") {
//                   return "Delivery fee";
//                 } else if (item?.type === "pickup_fee") {
//                   return `Pickup Fee`;
//                 } else if (item?.date === "Wedding") {
//                   return "Wedding";
//                 } else {
//                   return item?.date;
//                 }
//               };
//               return (
//                 <div key={idx} className="estimate-description__table__col">
//                   <span>{findText()}</span>
//                   <span>{currencyFormat(item?.price)}</span>
//                 </div>
//               );
//             })}

//             <div className="estimate-description__table__col">
//               <h4>{t("estimate_total")}</h4>
//               <h4>{currencyFormat(priceCalculate?.total)}</h4>
//             </div>
//           </div>
//         </div>
//       </div>
//       {type === "transfer_service" ? (
//         <div className="price_note">
//           <i className="fa-solid fa-circle-exclamation"></i>{" "}
//           <p>{t("the_price_includes")}</p>
//         </div>
//       ) : (
//         <div className="price_note">
//           <i className="fa-solid fa-circle-exclamation"></i>{" "}
//           <p>{t("the_price_does_not")}</p>
//         </div>
//       )}
//     </>
//   );
// }

// export default PriceEstimate;
